/* Save & Email My Gangsheet and Find My Gangsheets (0.3e Stage B).

   - EmailFlow: the window that asks for the address, then the 6-digit code (Save & Email, or Find My
     Gangsheets on any device). The code works only in this browser, for 10 minutes.
   - SaveButton / SaveEmailBody: the top bar's Save, which now also says where the gangsheet is saved
     (this browser, or an email) and offers Save & Email, Email me the steps and Remove from my email.
   - LostDialog: a gangsheet that can't be opened here, with the way back (Find My Gangsheets).
   Everything here only shows while the server has the email features on for this browser. */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, type EmailChallenge, type EmailStart } from '../api';
import {
  useStore, toast, flushSave, emailOn, openEmailFlow, saveOpenToEmail, emailVerified, signOutEmail, removeFromEmail, emailSteps, newProject
} from '../store/store';
import { navigate } from '../router';
import { Icon } from './icons';
import { Dialog } from './primitives';

const host = () => (typeof location !== 'undefined' ? location.host : 'build.printees.co.nz');

/** What the address is used for, and how long gangsheets are kept. */
export function PrivacyNote() {
  const url = useStore(s => s.config?.email?.privacyUrl);
  return (
    <p className="tiny muted em-privacy">
      We only use your email to send codes and let you open your saved gangsheets on any device. No marketing.
      Saved gangsheets are kept for at least 12 months after your last change.
      {url ? <> <a href={url} target="_blank" rel="noreferrer">Privacy policy</a></> : null}
    </p>
  );
}

/* ----------------------------------------------------------- the window */
export function EmailFlow() {
  const flow = useStore(s => s.emailFlow);
  if (!flow) return null;
  return <EmailFlowDialog key={flow.purpose} purpose={flow.purpose} />;
}

function EmailFlowDialog({ purpose }: { purpose: 'save' | 'find' }) {
  const pid = useStore(s => s.projectId);
  const days = useStore(s => s.config?.email?.sessionDays || 30);
  const [step, setStep] = useState<'email' | 'code' | 'done'>('email');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ch, setCh] = useState<(EmailStart & { at: number; finished?: boolean }) | null>(null);
  const [code, setCode] = useState('');
  const [send, setSend] = useState<EmailChallenge | null>(null);
  const [now, setNow] = useState(Date.now());
  const [saved, setSaved] = useState<{ savedTo: string; note: string | null } | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const close = () => useStore.setState({ emailFlow: null });

  // the resend countdown
  useEffect(() => {
    if (step !== 'code') return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [step]);
  // did the email go? (Resend can refuse an address, or the day's emails can run out)
  useEffect(() => {
    if (!ch) return;
    let stop = false, n = 0;
    setSend(null);
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api.emailChallenge(ch.challengeId);
        if (stop) return;
        setSend(r);
        if (r.status === 'sending' && n++ < 25) setTimeout(poll, 1200);
      } catch { /* the code still works if the email arrives */ }
    };
    setTimeout(poll, 600);
    return () => { stop = true; };
  }, [ch?.challengeId]);
  useEffect(() => { if (step === 'code') codeRef.current?.focus(); }, [step, ch?.challengeId]);

  const ask = async () => {
    if (!email.trim()) { setErr('Type your email address.'); return; }
    setErr(null); setBusy(true);
    try {
      if (purpose === 'save') await flushSave();
      const r = await api.emailStart(email.trim(), purpose, purpose === 'save' ? useStore.getState().projectId || undefined : undefined);
      setCh({ ...r, at: Date.now() });
      setCode('');
      setStep('code');
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const verify = async (value = code) => {
    if (!ch || busy) return;
    if (value.length !== 6) { setErr('Enter the 6-digit code from the email.'); return; }
    setErr(null); setBusy(true);
    try {
      const r = await api.emailVerify(ch.challengeId, value);
      emailVerified(r);
      if (purpose === 'find') {
        close();
        if (location.pathname !== '/my') navigate('/my');
        toast('Signed in as ' + (r.session.email || 'your email') + '. Your saved gangsheets are listed here.', { ms: 7000 });
      } else if (r.saved) {
        setSaved({ savedTo: r.saved.savedTo, note: r.confirmation === 'sent' ? null : r.confirmationMessage });
        setStep('done');
      } else {
        setErr('We couldn’t save this gangsheet to that email. Reload the page and try again.');
      }
    } catch (e) {
      const a = e as ApiError;
      setErr(a.message);
      setCode('');
      if (a.data?.expired) setCh(c => (c ? { ...c, finished: true } : c));
    } finally { setBusy(false); }
  };

  const left = ch ? Math.max(0, Math.ceil((ch.at + ch.resendInSec * 1000 - now) / 1000)) : 0;
  const mmss = (n: number) => Math.floor(n / 60) + ':' + String(n % 60).padStart(2, '0');
  const title = purpose === 'save' ? 'Save & Email My Gangsheet' : 'Find My Gangsheets';

  return (
    <Dialog title={title} onClose={close} className="em-dlg">
      <span className="kicker">{title}</span>
      {step === 'email' && (
        <form noValidate onSubmit={e => { e.preventDefault(); void ask(); }}>
          <h2>{purpose === 'save' ? 'Get this gangsheet back on any device' : 'Open gangsheets saved to your email'}</h2>
          <p className="muted small">{purpose === 'save'
            ? 'We’ll email you a 6-digit code to check it’s your address. No password needed.'
            : 'Enter the email you saved them to. We’ll send you a code. This works on any device, including this one.'}</p>
          <label className="field em-field">
            <span className="label">Email</span>
            <input className={'in' + (err ? ' is-bad' : '')} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false}
              placeholder="you@example.com" value={email} maxLength={254} onChange={e => { setEmail(e.target.value); setErr(null); }} data-em="email" />
          </label>
          {err && <p className="callout callout-bad em-err" role="alert"><Icon name="alert" /><span>{err}</span></p>}
          <PrivacyNote />
          <div className="dialog-actions">
            <button type="button" className="btn btn-line btn-sm" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-em="send">{busy ? <span className="spin" /> : <Icon name="mail" />}Send code</button>
          </div>
        </form>
      )}

      {step === 'code' && ch && (
        <form onSubmit={e => { e.preventDefault(); void verify(); }}>
          <h2>Enter the code we sent to {ch.sentTo}</h2>
          {send?.status === 'failed' ? (
            <p className="callout callout-bad em-err" role="alert"><Icon name="alert" /><span>{send.message}</span></p>
          ) : (
            <p className="small muted em-sent">
              {send?.status === 'sent' ? <><Icon name="okCircle" />Sent. It can take a minute; check your spam folder too.</> : <><span className="spin" />Sending…</>}
            </p>
          )}
          <label className="field em-field">
            <span className="label">6-digit code</span>
            <input ref={codeRef} className={'in em-code num' + (err ? ' is-bad' : '')} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*"
              placeholder="••••••" value={code} disabled={ch.finished} aria-describedby="em-code-help" data-em="code"
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(v);
                if (v.length === 6 && !busy) void verify(v);
              }} />
          </label>
          {err && <p className="callout callout-bad em-err" role="alert"><Icon name="alert" /><span>{err}</span></p>}
          <p id="em-code-help" className="tiny muted em-help">
            The code works for 10 minutes, in this browser only. Didn’t get it?{' '}
            {left > 0 ? <>You can send a new code in <span className="num">{mmss(left)}</span>.</>
              : <button type="button" className="link small" onClick={() => void ask()} disabled={busy} data-em="resend">Send a new code</button>}
            {' · '}<button type="button" className="link small" onClick={() => { setStep('email'); setErr(null); setCh(null); }}>Use a different email</button>
          </p>
          <div className="dialog-actions">
            <button type="button" className="btn btn-line btn-sm" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || ch.finished || code.length !== 6} data-em="verify">
              {busy ? <span className="spin" /> : <Icon name="check" />}{purpose === 'save' ? 'Verify and save' : 'Verify'}
            </button>
          </div>
        </form>
      )}

      {step === 'done' && saved && (
        <div>
          <span className="em-ok" aria-hidden="true"><Icon name="check" /></span>
          <h2>Saved to {saved.savedTo}</h2>
          <p className="small muted">To open it on another device, go to <b>{host()}</b> and choose <b>Find My Gangsheets</b>. {saved.note ? '' : 'We’ve emailed you these steps too.'}</p>
          {saved.note && <p className="callout callout-warn em-err"><Icon name="alert" /><span>{saved.note.replace('We can’t send emails right now', 'We couldn’t email you the steps')}</span></p>}
          <p className="tiny muted" style={{ marginTop: 10 }}>This device stays signed in for {days} days, so new gangsheets are saved to your email too. <button className="link small" onClick={() => { close(); void signOutEmail(false); }}>Sign out</button></p>
          <div className="dialog-actions">
            <button className="btn btn-primary btn-sm" onClick={close} data-em="done">Done</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/* ----------------------------------------------------- where it's saved */
function ago(at?: number): string {
  if (!at) return '';
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return Math.round(s / 60) + ' min ago';
  return new Date(at).toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' });
}

export function SignedInLine({ className = '' }: { className?: string }) {
  const email = useStore(s => s.email);
  if (!email?.signedIn) return null;
  return (
    <p className={'tiny muted em-signed ' + className}>
      <Icon name="mail" />Signed in as <b>{email.email}</b>
      <span className="em-signed-acts">
        <button className="link small" onClick={() => void signOutEmail(false)} data-em="signout">Sign out</button>
        <button className="link small" onClick={() => void signOutEmail(true)} data-em="signout-all">Sign out everywhere</button>
      </span>
    </p>
  );
}

/** The save panel's contents (top bar popover, or a window from the ⋯ menu on phones). */
export function SaveEmailBody({ onClose }: { onClose: () => void }) {
  const pid = useStore(s => s.projectId);
  const meta = useStore(s => s.meta);
  const save = useStore(s => s.save);
  const designs = useStore(s => s.doc.designs.length);
  const signedIn = useStore(s => !!s.email?.signedIn);
  const [remove, setRemove] = useState(false);
  return (
    <div className="em-body">
      <p className="em-status">
        <Icon name={meta.savedTo ? 'mail' : pid ? 'check' : 'info'} />
        <b>{meta.savedTo ? 'Saved to ' + meta.savedTo : pid ? 'Saved in this browser' : 'Not saved yet'}</b>
        {pid && save.at ? <span className="tiny muted"> · {ago(save.at)}</span> : null}
      </p>
      {!meta.savedTo ? (
        <>
          <p className="small muted">{pid ? 'Only this browser can open it. Save it to your email to get it back on any device.' : 'Add a design and it saves itself. Then you can save it to your email too.'}</p>
          <button className="btn btn-primary btn-sm btn-block" disabled={!designs} onClick={() => { onClose(); void saveOpenToEmail(); }} data-em="save-email">
            <Icon name="mail" />Save &amp; Email My Gangsheet
          </button>
          {!designs && <span className="tiny muted">Add a design first.</span>}
        </>
      ) : (
        <>
          <p className="small muted">Open it on any device: go to {host()} and choose Find My Gangsheets.</p>
          <div className="em-row">
            <button className="btn btn-line btn-sm" onClick={() => { onClose(); void emailSteps(pid!); }} data-em="steps"><Icon name="mail" />Email me the steps</button>
            <button className="btn btn-line btn-sm" onClick={() => setRemove(true)} data-em="remove"><Icon name="mailOff" />Remove from my email</button>
          </div>
        </>
      )}
      <hr />
      {signedIn ? <SignedInLine /> : (
        <button className="link small" onClick={() => { onClose(); openEmailFlow('find'); }} data-em="find"><Icon name="search" />Find My Gangsheets</button>
      )}
      {remove && pid && <RemoveDialog id={pid} name={useStore.getState().doc.name} madeHere={meta.madeHere} savedTo={meta.savedTo!} onClose={() => setRemove(false)} onDone={onClose} />}
    </div>
  );
}

/** Remove from my email: never deletes anything. */
export function RemoveDialog({ id, name, madeHere, savedTo, onClose, onDone }: { id: string; name: string; madeHere: boolean; savedTo: string; onClose: () => void; onDone?: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title="Remove from my email" onClose={onClose} className="rm-dlg">
      <h2>Remove “{name || 'this gangsheet'}” from {savedTo}?</h2>
      <p className="muted small">
        {madeHere
          ? 'It stays saved in this browser, but you won’t be able to open it on other devices with Find My Gangsheets. Nothing is deleted.'
          : 'It stays saved in the browser it was made in, but you won’t be able to open it on this device any more. Nothing is deleted.'}
      </p>
      <div className="dialog-actions">
        <button className="btn btn-line btn-sm" onClick={onClose}>Keep it saved</button>
        <button className="btn btn-danger btn-sm" disabled={busy} data-em="remove-confirm" onClick={async () => { setBusy(true); const ok = await removeFromEmail(id); setBusy(false); if (ok) { onClose(); onDone?.(); } }}>
          <Icon name="mailOff" />Remove from my email
        </button>
      </div>
    </Dialog>
  );
}

/** A small popover under a button (closes on outside click and Escape). */
function Popover({ button, children, className = '' }: { button: (open: boolean, toggle: () => void) => ReactNode; children: (close: () => void) => ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent | TouchEvent) => { if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as HTMLElement).closest?.('.scrim')) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.scrim')) setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('touchstart', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('touchstart', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className={'menu-wrap ' + className} ref={ref}>
      {button(open, () => setOpen(o => !o))}
      {open && <div className="em-pop" role="dialog" aria-label="Where this gangsheet is saved">{children(() => setOpen(false))}</div>}
    </div>
  );
}

/** The top bar's Save: saves now, and shows where the gangsheet is saved. */
export function SaveButton() {
  const pid = useStore(s => s.projectId);
  return (
    <Popover className="top-save-wrap no-phone" button={(open, toggle) => (
      <button className="btn btn-line btn-sm top-save" aria-expanded={open} aria-label="Save" data-tip="Saves automatically as you go. Click to save now and to save to your email"
        onClick={() => { if (pid) void flushSave(); toggle(); }}>
        <Icon name="save" /><span className="lbl">Save</span><Icon name="chevDown" className="top-save-chev" />
      </button>
    )}>{close => <SaveEmailBody onClose={close} />}</Popover>
  );
}

/** The same, as a window (from the ⋯ menu; phones have no Save button). */
export function SaveEmailDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog title="Save & Email My Gangsheet" onClose={onClose} className="em-dlg">
      <span className="kicker">Your gangsheet</span>
      <SaveEmailBody onClose={onClose} />
    </Dialog>
  );
}

/* ---------------------------------------------- a gangsheet that won't open */
export function LostDialog() {
  const lost = useStore(s => s.lost);
  if (!lost) return null;
  const on = emailOn();
  const close = () => useStore.setState({ lost: null });
  return (
    <Dialog title="We can’t open that gangsheet here" onClose={close} className="lost-dlg">
      <span className="kicker">{lost === 'session' ? 'Signed out' : 'Not in this browser'}</span>
      {lost === 'session' ? (
        <>
          <h2>Your email sign-in has ended</h2>
          <p className="muted small">This gangsheet was open through your email. Use Find My Gangsheets to get a new code and carry on. Changes you made since are kept in this browser and offered back when it opens.</p>
        </>
      ) : (
        <>
          <h2>We can’t open that gangsheet here</h2>
          <p className="muted small">
            It isn’t saved in this browser. Gangsheets stay in the browser they were made in, and a gangsheet’s link on its own doesn’t open it anywhere else.
            {on ? ' If you saved it to your email, Find My Gangsheets opens it here after a code.' : ''}
          </p>
        </>
      )}
      <div className="dialog-actions">
        <button className="btn btn-line btn-sm" onClick={() => { close(); if (lost === 'session') { newProject(); navigate('/my'); } }}>{lost === 'session' ? 'My gang sheets' : 'Start a new gangsheet'}</button>
        {on && <button className="btn btn-primary btn-sm" onClick={() => { close(); if (lost === 'session') newProject(); openEmailFlow('find'); }} data-em="lost-find"><Icon name="search" />Find My Gangsheets</button>}
      </div>
    </Dialog>
  );
}
